import { useMemo } from 'react';

import { type Board, type Library } from '@air/types';

import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type UseWorkspaceBoardsOptions, useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';

export type LocationsFilterBoard = Pick<Board, 'id' | 'title'> & {
  boards: Map<string, LocationsFilterBoard>;
  isVisualHierarchyBoard: boolean;
};

export type LocationsFilterLibrary = Pick<Library, 'id' | 'title' | 'color' | 'icon'> & {
  boards: Map<string, LocationsFilterBoard>;
};

export const useLocationsFilterData = ({ search }: { search?: string }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { libraries: librariesFlag } = usePrivateAddOnFeatures();
  const workspaceId = currentWorkspace?.id;

  const {
    data: librariesData,
    isLoading: isLibrariesLoading,
    hasNextPage: hasMoreLibraries,
    fetchNextPage: loadMoreLibraries,
    isFetchingNextPage: isLoadingMoreLibraries,
  } = useLibraries({
    options: search ? { search } : undefined,
  });

  const { boardsQueryOptions, boardsQueryEnabled } = useMemo<{
    boardsQueryOptions: UseWorkspaceBoardsOptions;
    boardsQueryEnabled: boolean;
  }>(
    () => ({
      boardsQueryOptions: search ? { search, view: 'all', libraryBoards: 'ALL' } : { view: 'root' },
      boardsQueryEnabled: Boolean(search || !librariesFlag),
    }),
    [librariesFlag, search],
  );

  const {
    data: workspaceBoards,
    hasMore: hasMoreBoards,
    loadNextPage: loadMoreBoards,
    isLoadingMore: isLoadingMoreBoards,
    isInitialLoading: isBoardsLoading,
  } = useWorkspaceBoards({
    workspaceId: boardsQueryEnabled ? workspaceId : undefined,
    ...boardsQueryOptions,
  });

  const result = useMemo<{ libraries: LocationsFilterLibrary[]; boards: Map<string, LocationsFilterBoard> }>(() => {
    const librariesByIdMap = new Map<
      string,
      Pick<Library, 'id' | 'title' | 'color' | 'icon'> & {
        boards: Board[];
      }
    >();

    librariesData?.pages
      .flatMap((page) => page.data)
      ?.forEach((library) => {
        librariesByIdMap.set(library.id, { ...library, boards: [] });
      });

    if (!search) {
      return {
        libraries: [...librariesByIdMap.values()].map((lib) => ({
          ...lib,
          boards: new Map(),
        })),
        boards: new Map(
          workspaceBoards.map((board) => [
            board.id,
            { id: board.id, title: board.title, isVisualHierarchyBoard: false, boards: new Map() },
          ]),
        ),
      };
    }

    const boards: Board[] = [];

    if (librariesFlag) {
      workspaceBoards.forEach((board) => {
        if (board.library) {
          const library = librariesByIdMap.get(board.library.id);
          if (library) {
            library.boards.push(board);
          } else {
            librariesByIdMap.set(board.library.id, { ...board.library, boards: [board] });
          }
        } else {
          boards.push(board);
        }
      });
    } else {
      boards.push(...workspaceBoards);
    }

    return {
      libraries: [...librariesByIdMap.values()].map((lib) => ({
        ...lib,
        boards: buildTree(lib.boards),
      })),
      boards: buildTree(boards),
    };
  }, [search, librariesData?.pages, librariesFlag, workspaceBoards]);

  return {
    ...result,
    hasMoreLibraries: !!hasMoreLibraries,
    loadMoreLibraries,
    isLoadingMoreLibraries,
    hasMoreBoards,
    loadMoreBoards,
    isLoadingMoreBoards,
    isLoading: isBoardsLoading || isLibrariesLoading,
  };
};

const buildTree = (boards: Board[]): Map<string, LocationsFilterBoard> => {
  const filterBoardsMap = new Map<string, LocationsFilterBoard>();
  const rootBoardIds = new Set<string>();
  const searchResultBoardIds = new Set<string>(boards.map((board) => board.id));

  const getOrCreateFilterBoard = ({ id, title, isVisualHierarchyBoard }: Omit<LocationsFilterBoard, 'boards'>) => {
    let filterBoard = filterBoardsMap.get(id);
    if (!filterBoard) {
      filterBoard = {
        id,
        title,
        isVisualHierarchyBoard,
        boards: new Map(),
      };
      filterBoardsMap.set(id, filterBoard);
    }

    return filterBoard;
  };

  boards.forEach((searchResultBoard) => {
    const boardPath = [
      ...(searchResultBoard.ancestors ?? []),
      {
        id: searchResultBoard.id,
        title: searchResultBoard.title,
      },
    ];

    boardPath.forEach((board, index) => {
      const filterBoard = getOrCreateFilterBoard({
        ...board,
        isVisualHierarchyBoard: !searchResultBoardIds.has(board.id),
      });
      if (index === 0) {
        rootBoardIds.add(filterBoard.id);
      }

      if (index < boardPath.length - 1) {
        const childBoard = getOrCreateFilterBoard({
          ...boardPath[index + 1],
          isVisualHierarchyBoard: !searchResultBoardIds.has(boardPath[index + 1].id),
        });
        filterBoard.boards.set(childBoard.id, childBoard);
      }
    });
  });

  return new Map([...filterBoardsMap.entries()].filter(([id]) => rootBoardIds.has(id)));
};
